import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../Supabase";
import { useNotifications } from "../components/NotificationCenter";
import { INPUT_LIMITS, clampText, clampFields } from "../lib/inputLimits";
import { getResetPasswordRedirect } from "../lib/authRedirects";

const LOGO_BUCKET = "logos";
const UPLOAD_LOGO_FUNCTION = "upload-logo";
const ALLOWED_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
]);
const ALLOWED_IMAGE_EXTENSIONS = new Set([
  "png",
  "jpeg",
  "jpg",
  "gif",
  "webp",
  "svg",
]);

const extractPathFromLogo = (input) => {
  if (!input) return null;

  let candidate = input;
  try {
    const url = new URL(input);
    candidate = url.pathname;
  } catch (e) {
    // Not a URL, use raw string
  }

  const bucketPattern = new RegExp(`/object/(?:public/)?${LOGO_BUCKET}/(.+)`);
  const match = candidate.match(bucketPattern);
  if (match?.[1]) return match[1];

  if (candidate.startsWith(`${LOGO_BUCKET}/`)) {
    return candidate.slice(`${LOGO_BUCKET}/`.length);
  }

  return candidate.replace(/^\/+/, "");
};

const resolveLogoUri = (raw) => {
  if (!raw) return null;

  if (/^data:|^blob:|^https?:\/\//.test(raw)) {
    return raw;
  }

  const path = extractPathFromLogo(raw);
  if (!path) return null;

  const { data } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);
  return data?.publicUrl || null;
};

const validateImageFile = (file) =>
  new Promise((resolve, reject) => {
    const extension = (file.name.split(".").pop() || "").toLowerCase();
    if (
      !file.type.startsWith("image/") ||
      !ALLOWED_IMAGE_TYPES.has(file.type) ||
      !ALLOWED_IMAGE_EXTENSIONS.has(extension)
    ) {
      reject(
        new Error(
          "Please upload a valid image (png, jpg, jpeg, gif, webp, or svg)."
        )
      );
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(true);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Selected file could not be read as an image."));
    };
    img.src = objectUrl;
  });

const ProfilePage = ({ user, onManageSubscription, onRequestFeature }) => {
  const { notify } = useNotifications();
  const limit = (value, key) =>
    clampText(value ?? "", INPUT_LIMITS[key] ?? 255);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState(null);

  const [email, setEmail] = useState(user?.email || "");
  const [logoPath, setLogoPath] = useState(null);
  const [logoUri, setLogoUri] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postCode, setPostCode] = useState("");
  const [payee, setPayee] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteError, setDeleteError] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [passwordErr, setPasswordErr] = useState(null);
  const [passwordInfo, setPasswordInfo] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleManageSubscription = () => {
    if (typeof onManageSubscription === "function") {
      onManageSubscription();
    } else {
      // Fallback if you ever render ProfilePage without the prop
      notify({
        type: "info",
        title: "Subscription",
        message: "This is where you would open your subscription settings.",
      });
    }
  };

  const handleRequestFeature = () => {
    if (typeof onRequestFeature === "function") {
      onRequestFeature();
    } else if (typeof window !== "undefined") {
      window.location.href = "/feature-request";
    }
  };

  const [initialState, setInitialState] = useState(null);

  useEffect(() => {
    if (!user) return;

    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: profErr } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        if (profErr) throw profErr;

        const profile = data || {};
        const storedLogoPath = limit(profile.logo_uri || null, "logo_uri");
        const resolvedLogoUri = resolveLogoUri(storedLogoPath);

        const next = {
          email: limit(profile.email || user.email || "", "email"),
          logoPath: storedLogoPath,
          logoUri: resolvedLogoUri,
          companyName: limit(profile.company_name || "", "companyName"),
          address: limit(profile.address || "", "address"),
          city: limit(profile.city || "", "city"),
          postCode: limit(profile.post_code || "", "postCode"),
          payee: limit(profile.payee || "", "payee"),
          sortCode: limit(profile.sort_code || "", "sortCode"),
          accountNumber: limit(profile.account_number || "", "accountNumber"),
        };

        setEmail(next.email);
        setLogoPath(next.logoPath);
        setLogoUri(next.logoUri);
        setCompanyName(next.companyName);
        setAddress(next.address);
        setCity(next.city);
        setPostCode(next.postCode);
        setPayee(next.payee);
        setSortCode(next.sortCode);
        setAccountNumber(next.accountNumber);
        setInitialState(next);
      } catch (e) {
        console.warn("Failed to load profile", e);
        setError(e?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const hasChanges = useMemo(() => {
    if (!initialState) return false;
    return (
      email !== initialState.email ||
      logoPath !== initialState.logoPath ||
      companyName !== initialState.companyName ||
      address !== initialState.address ||
      city !== initialState.city ||
      postCode !== initialState.postCode ||
      payee !== initialState.payee ||
      sortCode !== initialState.sortCode ||
      accountNumber !== initialState.accountNumber
    );
  }, [
    email,
    logoPath,
    companyName,
    address,
    city,
    postCode,
    payee,
    sortCode,
    accountNumber,
    initialState,
  ]);

  const requiredFilled = useMemo(() => {
    return (
      companyName.trim() &&
      address.trim() &&
      city.trim() &&
      postCode.trim() &&
      payee.trim() &&
      sortCode.trim() &&
      accountNumber.trim()
    );
  }, [companyName, address, city, postCode, payee, sortCode, accountNumber]);

  const canSave = !!user && requiredFilled && hasChanges && !saving && !loading;
  const isProfileComplete = !!user && requiredFilled;

  const handleReset = () => {
    if (!initialState) return;
    setEmail(initialState.email);
    setLogoPath(initialState.logoPath);
    setLogoUri(initialState.logoUri);
    setCompanyName(initialState.companyName);
    setAddress(initialState.address);
    setCity(initialState.city);
    setPostCode(initialState.postCode);
    setPayee(initialState.payee);
    setSortCode(initialState.sortCode);
    setAccountNumber(initialState.accountNumber);
    setError(null);
  };

  const handleSave = async () => {
    if (!user || !canSave) return;

    try {
      setSaving(true);
      setError(null);

      const digits = sortCode.replace(/\D/g, "").slice(0, 6);
      const formattedSortCode =
        digits.length === 6
          ? `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`
          : sortCode;

      const sanitized = clampFields(
        {
          id: user.id,
          email: email.trim(),
          logo_uri: logoPath || logoUri || null,
          company_name: companyName.trim(),
          address: address.trim(),
          city: city.trim(),
          post_code: postCode.trim(),
          payee: payee.trim(),
          sort_code: formattedSortCode.trim(),
          account_number: accountNumber.trim(),
        },
        {
          email: INPUT_LIMITS.email,
          logo_uri: 255,
          company_name: INPUT_LIMITS.companyName,
          address: INPUT_LIMITS.address,
          city: INPUT_LIMITS.city,
          post_code: INPUT_LIMITS.postCode,
          payee: INPUT_LIMITS.payee,
          sort_code: INPUT_LIMITS.sortCode,
          account_number: INPUT_LIMITS.accountNumber,
        }
      );

      const { data, error: upsertErr } = await supabase
        .from("profiles")
        .upsert(
          sanitized,
          { onConflict: "id" }
        )
        .select()
        .single();

      if (upsertErr) throw upsertErr;

      const savedLogoPath = data.logo_uri || null;
      const savedLogoUri = resolveLogoUri(savedLogoPath);

      const next = {
        email: data.email || "",
        logoPath: savedLogoPath,
        logoUri: savedLogoUri,
        companyName: data.company_name || "",
        address: data.address || "",
        city: data.city || "",
        postCode: data.post_code || "",
        payee: data.payee || "",
        sortCode: data.sort_code || "",
        accountNumber: data.account_number || "",
      };

      setInitialState(next);
      setLogoUri(savedLogoUri);
      setLogoPath(savedLogoPath);
    } catch (e) {
      console.warn("Failed to save profile", e);
      setError(e?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      notify({
        type: "error",
        title: "Sign out failed",
        message: e?.message || "Please try again.",
      });
    }
  };

  const handleChangePasswordRequest = async () => {
    if (!user?.email || passwordLoading) return;

    try {
      setPasswordErr(null);
      setPasswordInfo(null);
      setPasswordLoading(true);

      const redirectTo = getResetPasswordRedirect();

      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo,
      });

      if (error) throw error;

      setPasswordInfo(
        "Password reset email sent. Follow the link to set a new password."
      );
    } catch (e) {
      console.warn("Password reset request failed", e);
      setPasswordErr(
        e?.message || "Could not send password reset email right now."
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id || deleteConfirm !== "DELETE" || deletingAccount) return;

    try {
      setDeletingAccount(true);
      setDeleteError(null);
      setDeleteInfo(null);

      const { data, error } = await supabase.functions.invoke(
        "delete-account",
        { body: {} }
      );

      if (error) throw error;
      if (!data?.success) {
        throw new Error(
          data?.error || "Could not delete account. Please try again."
        );
      }

      setDeleteInfo("Account deleted. Signing out…");
      await supabase.auth.signOut();
      if (typeof window !== "undefined") {
        window.location.href = "/";
      }
    } catch (e) {
      console.warn("Delete account failed", e);
      setDeleteError(e?.message || "Failed to delete account.");
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleLogoFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !user) return;

    try {
      setUploadingLogo(true);
      setError(null);

      await validateImageFile(file);

      const formData = new FormData();
      formData.append("logo", file);
      const safeEmail = limit(email || user.email || "", "email");
      if (safeEmail) {
        formData.append("email", safeEmail);
      }

      const { data, error: fnError } = await supabase.functions.invoke(
        UPLOAD_LOGO_FUNCTION,
        {
          body: formData,
        }
      );

      if (fnError) {
        const message =
          fnError?.context?.error ||
          fnError?.message ||
          "Failed to upload logo.";
        throw new Error(message);
      }

      const nextLogoPath = extractPathFromLogo(
        data?.logoPath || data?.logo_uri || null
      );
      if (!nextLogoPath) {
        throw new Error("Upload failed to return a logo path.");
      }

      const nextLogoUri =
        data?.publicUrl || resolveLogoUri(nextLogoPath) || logoUri;

      if (!nextLogoUri) {
        throw new Error("Could not get public URL for logo.");
      }

      setLogoUri(nextLogoUri);
      setLogoPath(nextLogoPath);
      setInitialState((prev) =>
        prev
          ? { ...prev, logoUri: nextLogoUri, logoPath: nextLogoPath }
          : prev
      );
    } catch (e) {
      console.error("Logo upload failed", e);
      setError(e?.message || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm text-sm text-slate-600">
          Loading profile…
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Profile</h1>
        <p className="text-sm text-slate-500">
          Manage your company details, logo, and bank information.
        </p>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {logoUri ? (
                <img
                  src={logoUri}
                  alt="Company logo"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-[10px] font-semibold text-slate-400 text-center px-1">
                  No logo
                </span>
              )}
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Business profile
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">
                {companyName || "Your company name"}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {email || user?.email}
              </div>

              <div className="mt-2">
                <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-medium text-slate-700 cursor-pointer hover:bg-slate-100">
                  <span>
                    {uploadingLogo
                      ? "Uploading logo…"
                      : logoUri
                      ? "Change logo"
                      : "Upload logo"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={handleLogoFileChange}
                    disabled={uploadingLogo}
                  />
                </label>
                <p className="mt-1 text-[11px] text-slate-400">
                  This logo will appear on your invoices.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap gap-2 md:mt-0">
            {city || postCode ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] text-slate-700">
                {[city, postCode].filter(Boolean).join(" · ")}
              </span>
            ) : null}
            <span
              className={[
                "rounded-full px-2.5 py-1 text-[11px] font-semibold",
                isProfileComplete
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700",
              ].join(" ")}
            >
              {isProfileComplete ? "Profile complete" : "Missing details"}
            </span>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <LabeledInput
            label="Email"
            value={email}
            onChange={(v) => setEmail(limit(v, "email"))}
            disabled
            maxLength={INPUT_LIMITS.email}
            helper="Email is managed by Supabase auth."
          />
          <LabeledInput
            label="Company name"
            value={companyName}
            onChange={(v) => setCompanyName(limit(v, "companyName"))}
            maxLength={INPUT_LIMITS.companyName}
          />
          <LabeledInput
            label="Address"
            value={address}
            onChange={(v) => setAddress(limit(v, "address"))}
            maxLength={INPUT_LIMITS.address}
          />
          <LabeledInput
            label="City"
            value={city}
            onChange={(v) => setCity(limit(v, "city"))}
            maxLength={INPUT_LIMITS.city}
          />
          <LabeledInput
            label="Post code"
            value={postCode}
            onChange={(v) => setPostCode(limit(v, "postCode"))}
            maxLength={INPUT_LIMITS.postCode}
          />
        </div>

        <div className="mt-4 grid gap-3 border-t border-slate-100 pt-4 md:grid-cols-3">
          <LabeledInput
            label="Payee name"
            value={payee}
            onChange={(v) => setPayee(limit(v, "payee"))}
            maxLength={INPUT_LIMITS.payee}
          />
          <LabeledInput
            label="Sort code"
            value={sortCode}
            onChange={(v) => setSortCode(limit(v, "sortCode"))}
            placeholder="12-34-56"
            maxLength={INPUT_LIMITS.sortCode}
          />
          <LabeledInput
            label="Account number"
            value={accountNumber}
            onChange={(v) => setAccountNumber(limit(v, "accountNumber"))}
            placeholder="12345678"
            maxLength={INPUT_LIMITS.accountNumber}
          />
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-slate-500">
            These details can be used in your invoice footer for bank transfers.
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={[
                "inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold",
                canSave
                  ? "bg-black text-white hover:bg-slate-800"
                  : "cursor-not-allowed bg-slate-200 text-slate-500",
              ].join(" ")}
            >
              {saving ? "Saving…" : hasChanges ? "Save changes" : "Saved"}
            </button>
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className={[
                "inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold border",
                hasChanges
                  ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400",
              ].join(" ")}
            >
              Reset
            </button>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-2 border-t border-slate-100 pt-4 md:flex-row md:items-center md:justify-between">
          <div className="text-xs text-slate-500">
            Signed in as <span className="font-medium">{user?.email}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleManageSubscription}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Manage subscription
            </button>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Change password
            </h2>
            <p className="text-xs text-slate-500">
              We&apos;ll email you a secure link to set a new password.
            </p>
          </div>
          <button
            type="button"
            onClick={handleChangePasswordRequest}
            disabled={passwordLoading}
            className={[
              "inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold",
              passwordLoading
                ? "cursor-not-allowed bg-slate-200 text-slate-500"
                : "bg-black text-white hover:bg-slate-800",
            ].join(" ")}
          >
            {passwordLoading ? "Sending…" : "Send reset link"}
          </button>
        </div>
        {passwordErr && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {passwordErr}
          </div>
        )}
        {passwordInfo && (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            {passwordInfo}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-red-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-red-700">Delete account</h2>
            <p className="text-xs text-slate-500">
              Permanently delete your account and associated data. This cannot be undone.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDeleteAccount}
            disabled={deleteConfirm !== "DELETE" || deletingAccount}
            className={[
              "inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold border",
              deleteConfirm !== "DELETE" || deletingAccount
                ? "cursor-not-allowed border-red-100 bg-red-50 text-red-400"
                : "border-red-200 bg-red-600 text-white hover:bg-red-700",
            ].join(" ")}
          >
            {deletingAccount ? "Deleting…" : "Delete account"}
          </button>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <label className="text-xs font-medium text-slate-700">
              Type DELETE to confirm
            </label>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="mt-1 w-full rounded-xl border border-red-200 px-3 py-2 text-sm outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
              placeholder="DELETE"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              We will remove your profile, invoices, customers, jobs, and sign you out.
            </p>
          </div>
        </div>
        {deleteError && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {deleteError}
          </div>
        )}
        {deleteInfo && (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            {deleteInfo}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Request a feature
            </h2>
            <p className="text-xs text-slate-500">
              Have an idea? Send it to contact@invoiceu.co.uk and we will take a
              look.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleRequestFeature}
              className="inline-flex items-center justify-center rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Request a feature
            </button>
            <a
              className="inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              href="mailto:contact@invoiceu.co.uk"
            >
              Email us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const LabeledInput = ({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  helper,
  maxLength,
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-slate-700">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder || label}
      maxLength={maxLength}
      className={[
        "w-full rounded-xl border px-3 py-2 text-sm outline-none",
        disabled
          ? "border-slate-200 bg-slate-50 text-slate-400"
          : "border-slate-300 bg-white text-slate-900 focus:border-slate-500 focus:ring-1 focus:ring-slate-500",
      ].join(" ")}
    />
    {helper && <p className="text-[11px] text-slate-400">{helper}</p>}
  </div>
);

export default ProfilePage;
